import { useEffect, useState } from "react";
import { useTranslations } from "../i18n/LanguageProvider";
import { Header } from "../components/ui/header";
import { money } from "../utilities/currency";
import axios from "axios";
import { Footer } from "../components/ui/footer";
import { CheckCircle, Smartphone, Calendar, CreditCard, ShieldCheck, BarChart3 } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import SalesBookingModal from "./SalesBookingModal.tsx";


type VippsBookingPageProps = {
    currentUser: any,
    onLogout: any,
    baseUrl: string,
    onLoginClick: any,
    onLoginSuccess: any,
    client: any,
}
export default function VippsBookingPage({ currentUser, onLogout, baseUrl, onLoginClick, onLoginSuccess, client }: VippsBookingPageProps) {
    const [wallet, setWallet] = useState(0);
    const [scrolled, setScrolled] = useState(false);

  const T = useTranslations("home");

    useEffect(() => {
        if (currentUser?.role === "customer") {
            axios.get(baseUrl + "/getCustomerWallet", { params: { token: currentUser.token } })

                .then((response) => {
                    if (response.data.status === "OK") {
                        setWallet(response.data.data.remaining_amount);
                    }
                });
        }
    }, [currentUser, baseUrl])

    const theme_settings = client ? JSON.parse(client.theme_settings) : {};

    const primaryColor = theme_settings.primaryColor || "#7c3aed"; // default purple-700
    const secondaryColor = theme_settings.secondaryColor || "#ec4899";

    const [appearance, setAppearance] = useState({
        gradientEnabled: false,
        gradientFrom: `${primaryColor}`,
        gradientTo: `${secondaryColor}`,
    });

    useEffect(() => {
        setAppearance(prev => ({
            gradientFrom: theme_settings.primaryColor,
            gradientTo: theme_settings.secondaryColor,
            gradientEnabled: theme_settings.useGradient === "true",
        }));
    }, [theme_settings.useGradient]);

    const closeAllModals = () => {
        setOpenDemo(false);
        setOpenSales(false);
        setShowKAM(false);
    };
    const [openDemo, setOpenDemo] = useState(false);
    const [openSales, setOpenSales] = useState(false);
    const [showKAM, setShowKAM] = useState(false);

    // Close modals on ESC (priority: KAM -> Success -> Demo -> Sales)
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key !== "Escape") return;
            if (showKAM) return void setShowKAM(false);
            if (openDemo) return void setOpenDemo(false);
            if (openSales) return void setOpenSales(false);
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [showKAM, openDemo, openSales]);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === "Escape") {
                closeAllModals();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);

    return (
        <div style={{
            "--primary-color": primaryColor,
            "--secondary-color": secondaryColor,
        }} className={"bg-white text-gray-900"}>
            <Header wallet={wallet} scrolled={scrolled} T={T} onLoginClick={onLoginClick} currentUser={currentUser} onLogout={onLogout} baseUrl={baseUrl} />
            <main className="min-h-screen bg-gray-50 text-gray-800 py-16 px-6 md:px-12 lg:px-24">
                <Link
                    to="/"
                    className="mb-6 text-[var(--primary-color)] hover:text-dark text-sm font-medium"
                >
                    ← Tilbake
                </Link>
                {/* HERO */}
                <section className="grid md:grid-cols-2 gap-12 px-10 py-24 max-w-7xl mx-auto items-center">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                        <h1 className="text-5xl font-bold leading-tight mb-6">
                            Bestill time.<br />
                            <span className="text-[var(--primary-color)]">Betal med Vipps.</span>
                        </h1>
                        <p className="text-xl text-gray-600 mb-8">
                            Ingen skjemaer. Ingen stress. <br />Bare bekreftet time – ferdig betalt.
                        </p>
                        <div className="flex gap-4">
                            <button className="flex items-center rounded-2xl text-white px-8 py-2 shadow transition hover:brightness-90" style={{ backgroundColor: "var(--primary-color)" }}>
                                Bestill time med Vipps
                            </button>
                            <button className="flex items-center rounded-2xl text-dark px-8 py-2 shadow transition hover:brightness-90" style={{ backgroundColor: "var(--secondary-color)" }}>
                                Se hvordan det fungerer
                            </button>
                        </div>
                        <p className="mt-6 text-sm text-gray-500">Forhåndsautorisert betaling · Automatisk faktura · Full rapportering</p>
                    </motion.div>

                    {/* IMAGE PLACEHOLDER */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6 }}
                        className="relative rounded-3xl bg-gray-200 h-[420px] flex items-center overflow-hidden justify-center"
                    >
                        <img
                            src="../../vipps-hero-image.png"
                            alt="Aktuelle kampanjer"
                            className="absolute inset-0 h-full w-full
                                        object-contain sm:object-cover
                                        object-center lg:object-left"
                        />
                        <span className="text-gray-500">Vipps hero image</span>
                    </motion.div>
                </section>

                {/* HOW IT WORKS */}
                <section className="px-10 py-20 max-w-7xl mx-auto">
                    <h2 className="text-4xl font-semibold mb-14 text-center">Slik fungerer det</h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            { icon: Smartphone, title: "Bestill med Vipps", text: "Kun mobilnummer. Ferdig på sekunder." },
                            { icon: ShieldCheck, title: "Automatisk registrering", text: "Kundedata hentes trygt via Vipps." },
                            { icon: CreditCard, title: "Forhåndsautorisert betaling", text: "Reservert – ikke trukket." },
                            { icon: Calendar, title: "Gjennomfør timen", text: "Telefon, video eller møte." },
                            { icon: CheckCircle, title: "Automatisk betaling", text: "Trekkes etter gjennomført time." },
                            { icon: BarChart3, title: "Full oversikt", text: "Kvittering, faktura og rapportering." },
                        ].map((step, i) => (
                            <Card key={i} className="rounded-2xl shadow-sm">
                                <CardContent className="p-8">
                                    <step.icon className="w-8 h-8 text-orange-500 mb-4" />
                                    <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                                    <p className="text-gray-600">{step.text}</p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </section>

                {/* NO SHOW POLICY */}
                <section className="px-10 py-16 bg-gray-100">
                    <div className="max-w-4xl mx-auto text-center">
                        <h3 className="text-2xl font-semibold mb-4">Tydelige og rettferdige vilkår</h3>
                        <p className="text-gray-700">
                            Avbestilling må skje senest <b>24 timer før avtalt tid</b>. Ved uteblivelse eller sen avbestilling
                            belastes timen i sin helhet via forhåndsautorisert betaling.
                        </p>
                    </div>
                </section>

                {/* FOOTER CTA */}
                <section className="px-10 py-24 max-w-5xl mx-auto text-center">
                    <h2 className="text-4xl font-bold mb-6">Klar for friksjonsfri booking?</h2>
                    <p className="text-lg text-gray-600 mb-10">Gi kundene dine den enkleste opplevelsen de kjenner: Vipps.</p>
                    <button onClick={() => setOpenDemo(true)} className="inline-flex items-center rounded-2xl text-white px-8 py-2 shadow transition hover:brightness-90" style={{ backgroundColor: "var(--primary-color)" }}>
                        Kom i gang med Chamcore
                    </button>
                    <p className="mt-6 text-sm text-gray-500">Levert av Chamcore – én plattform for booking, betaling og kundedata.</p>
                </section>
            </main>
            <Footer baseUrl={baseUrl} />
            {/* Sales booking modal */}
            <SalesBookingModal
                open={openDemo}
                onClose={() => setOpenDemo(false)}
                baseUrl={baseUrl}
                client={client}
            />
        </div>
    );
}
